import { server, url } from '@/helpers';
import { paymentCardBrandSchema } from '@/types/paymentcards';
import { z } from 'zod';

const PaymentMethodBrandSchema = z.enum(['amex', 'diners', 'discover', 'mc', 'visa']);

const PaymentMethodTypeSchema = z.enum(['Cards', 'GooglePay', 'Klarna', 'Swish', 'Walley', 'ApplePay']);

const PaymentMethodConfigurationsSchema = z.object({
  merchantId: z.string().optional(),
  gatewayMerchantId: z.string().optional(),
});

const MinimumAmountRuleSchema = z.object({ id: z.string(), minimumAmount: z.number() });

export const PaymentMethodSchema = z.object({
  type: PaymentMethodTypeSchema,
  m3PaymentMthodId: z.number(),
  name: z.string(),
  brands: z.array(z.string()).nullable(),
  configurations: PaymentMethodConfigurationsSchema.nullable(),
  authorizationValidityInDays: z.number().nullable(),
  mpType: z.number().optional(),
  minimumAmounts: z.array(MinimumAmountRuleSchema).nullable(),
});

const StoredPaymentMethodSchema = z.object({
  brand: paymentCardBrandSchema.catch('unknown'),
  expiryMonth: z.string(),
  expiryYear: z.string(),
  holderName: z.string(),
  id: z.string(),
  lastFour: z.string(),
  name: z.string(),
});

const PaymentMethodsSchema = z.object({
  paymentMethods: z.array(PaymentMethodSchema),
  storedPaymentMethods: z.array(StoredPaymentMethodSchema),
});

const PlatformFeeSchema = z.object({
  enabled: z.boolean(),
  amount: z.number().nullable(),
  bokadirektShare: z.number().nullable(),
  merchantShare: z.number().nullable(),
});

const MerchantSettingsSchema = z.object({
  platformFee: PlatformFeeSchema,
  paymentMethods: PaymentMethodsSchema,
  dealsEnabled: z.boolean(),
  isBokadirektPayMerchant: z.boolean(),
});

export type PaymentMethodBrand = z.infer<typeof PaymentMethodBrandSchema>;
export type PaymentMethodType = z.infer<typeof PaymentMethodTypeSchema>;
export type PaymentMethodConfigurations = z.infer<typeof PaymentMethodConfigurationsSchema>;
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;
export type PaymentMethods = z.infer<typeof PaymentMethodsSchema>;
export type StoredPaymentMethod = z.infer<typeof StoredPaymentMethodSchema>;
export type MinimumAmountRule = z.infer<typeof MinimumAmountRuleSchema>;
export type PlatformFee = z.infer<typeof PlatformFeeSchema>;
export type MerchantSettings = z.infer<typeof MerchantSettingsSchema>;

const TokenizeCardSessionResponseSchema = z.object({
  success: z.boolean(),
  errors: z.array(z.unknown()),
  data: z.object({
    sessionId: z.string(),
    sessionData: z.string(),
    expiresAt: z.string(),
    merchantReference: z.string(),
    shopperReference: z.string(),
    htmlSnippet: z.string(),
  }),
});

export type TokenizeCardSessionResponse = z.infer<typeof TokenizeCardSessionResponseSchema>;

const GetTokenizedCardsRequestSchema = z.object({
  Country: z.object({
    CountryCode: z.string(),
    Currency: z.string(),
    CultureCode: z.string(),
  }),
  ShopperReference: z.string(),
});

const GetTokenizedCardsResponseSchema = z.object({
  success: z.boolean(),
  errors: z.array(z.unknown()),
  data: z.array(StoredPaymentMethodSchema),
});

export type GetTokenizedCardsRequest = z.infer<typeof GetTokenizedCardsRequestSchema>;
export type GetTokenizedCardsResponse = z.infer<typeof GetTokenizedCardsResponseSchema>;

/**
 * Get merchant settings including payment methods and booking fee configuration for a place and employee
 */
function getMerchantSettings(placeId: number, employeeId: number, signal?: AbortSignal): Promise<MerchantSettings> {
  return server.request
    .get(`/places/${placeId}/${employeeId}/merchant-settings`, { signal })
    .then(server.handleSuccess)
    .then((data) => MerchantSettingsSchema.parse(data));
}

function getMerchantSettingsByOrg(
  organisationNumber: string,
  placeId?: number | null,
  signal?: AbortSignal,
): Promise<MerchantSettings> {
  return server.request
    .get(`/merchant-settings/org/${organisationNumber}`, {
      params: placeId ? { placeId } : undefined,
      signal,
    })
    .then(server.handleSuccess)
    .then((data) => MerchantSettingsSchema.parse(data));
}

/**
 * Create a tokenize card session
 * Creates a session for tokenizing a card without making a payment
 */
function createTokenizeCardSession(): Promise<TokenizeCardSessionResponse> {
  return server.request
    .post('/payments/tokenize-card')
    .then(server.handleSuccess)
    .then((data) => TokenizeCardSessionResponseSchema.parse(data));
}

/**
 * Get tokenized cards for a shopper
 * Retrieves the list of stored payment methods (tokenized cards) for a specific ShopperReference
 */
function getTokenizedCards(): Promise<GetTokenizedCardsResponse> {
  return server.request
    .get('/payments/get-tokenize-cards')
    .then(server.handleSuccess)
    .then((data) => GetTokenizedCardsResponseSchema.parse(data));
}

function waitForBokadirektPayNotification(channel: string): EventSource {
  const lang = localStorage.getItem('language') || 'sv';
  const eventSource = new EventSource(`${url.getServerUrl()}payments/sse?channel=${channel}&lng=${lang}`);
  return eventSource;
}

export const paymentsServices = {
  getMerchantSettings,
  getMerchantSettingsByOrg,
  createTokenizeCardSession,
  getTokenizedCards,
  waitForBokadirektPayNotification,
};
